/**
 * Agent Cards Panel Component
 * Implements PRD FR-34
 */

import { useSimulationStore } from '../state/simulationStore';
import { AgentState, TimeStep } from '../model/types';

interface AgentCardProps {
  title: string;
  state: AgentState;
  color: string;
  bgColor: string;
  n?: number; // Number of MMs for aggregate display
  currentTime: TimeStep;
}

function formatNumber(num: number, decimals: number = 4): string {
  return num.toFixed(decimals);
}

function AgentCard({ title, state, color, bgColor, n, currentTime }: AgentCardProps) {
  const { inventory, cash, cumulativePnL, riskExposure, tradeThisPeriod } = state;

  return (
    <div className={`rounded-lg shadow-md p-4 ${bgColor}`}>
      <h4 className={`font-semibold text-lg mb-3 ${color}`}>{title}</h4>

      <div className="space-y-2 text-sm">
        {/* Inventory */}
        <div className="flex justify-between">
          <span className="text-gray-600">Inventory:</span>
          <span className="font-mono font-medium">
            {formatNumber(inventory, 4)}
            {n && (
              <span className="text-gray-500 ml-1">
                (Agg: {formatNumber(inventory * n, 4)})
              </span>
            )}
          </span>
        </div>

        {/* Trade This Period */}
        {tradeThisPeriod !== 0 && (
          <div className="flex justify-between">
            <span className="text-gray-600">Trade (t={currentTime}):</span>
            <span
              className={`font-mono font-medium ${
                tradeThisPeriod > 0 ? 'text-green-600' : 'text-red-600'
              }`}
            >
              {tradeThisPeriod > 0 ? '+' : ''}
              {formatNumber(tradeThisPeriod, 4)}
              {n && (
                <span className="text-gray-500 ml-1">
                  (Agg: {tradeThisPeriod > 0 ? '+' : ''}
                  {formatNumber(tradeThisPeriod * n, 4)})
                </span>
              )}
            </span>
          </div>
        )}

        {/* Cash */}
        <div className="flex justify-between">
          <span className="text-gray-600">Cash:</span>
          <span className="font-mono">
            {formatNumber(cash, 2)}
            {n && (
              <span className="text-gray-500 ml-1">
                (Agg: {formatNumber(cash * n, 2)})
              </span>
            )}
          </span>
        </div>

        {/* P&L */}
        <div className="flex justify-between">
          <span className="text-gray-600">P&L:</span>
          <span
            className={`font-mono font-medium ${
              cumulativePnL >= 0 ? 'text-green-600' : 'text-red-600'
            }`}
          >
            {cumulativePnL >= 0 ? '+' : ''}
            {formatNumber(cumulativePnL, 4)}
            {n && (
              <span className="text-gray-500 ml-1">
                (Agg: {cumulativePnL >= 0 ? '+' : ''}
                {formatNumber(cumulativePnL * n, 4)})
              </span>
            )}
          </span>
        </div>

        {/* Risk Exposure */}
        {riskExposure > 0 && (
          <div className="flex justify-between">
            <span className="text-gray-600">Risk (Var):</span>
            <span className="font-mono text-amber-600">
              {formatNumber(riskExposure, 4)}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

export function AgentCardsPanel() {
  const { simulation } = useSimulationStore();

  if (!simulation) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="text-center text-gray-500">
          Run a simulation to see agent states
        </div>
      </div>
    );
  }

  const { agents, currentTime, params } = simulation;
  const mmState = agents.MM[currentTime];
  const lt1State = agents.LT1[currentTime];
  const lt2State = agents.LT2[currentTime];

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-gray-800">
        Agent States at t = {currentTime}
      </h3>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Market Makers */}
        <AgentCard
          title={`Market Makers (n = ${params.n})`}
          state={mmState}
          color="text-purple-700"
          bgColor="bg-purple-50"
          n={params.n}
          currentTime={currentTime}
        />

        {/* LT1 */}
        <AgentCard
          title="LT1 (Liquidity Trader 1)"
          state={lt1State}
          color="text-orange-700"
          bgColor="bg-orange-50"
          currentTime={currentTime}
        />

        {/* LT2 */}
        <AgentCard
          title="LT2 (Liquidity Trader 2)"
          state={lt2State}
          color="text-cyan-700"
          bgColor="bg-cyan-50"
          currentTime={currentTime}
        />
      </div>

      {/* Additional Context */}
      {currentTime === 1 && (
        <div className="text-sm text-gray-600 bg-gray-50 p-3 rounded-lg">
          <strong>Note:</strong> LT2 is not yet active at t=1. The order imbalance is
          absorbed by MMs and partially retained by LT1.
        </div>
      )}
    </div>
  );
}
